/**
 * KnowledgeList - Project knowledge base entry list
 *
 * Features:
 * - Display knowledge entries with category, phases, pinned/enabled state
 * - Enable/disable, edit, and delete each entry
 * - Empty state when no entries exist yet
 */

import { useState } from 'react';
import { BookOpen, Download, Paperclip, Pin, Plus, Trash2 } from 'lucide-react';
import { KnowledgeCategoryLabels, AgentPhaseLabels, type KnowledgeEntryResponse } from '@resolve-ai/shared';
import { Card } from '../common';
import { EditIcon } from '../icons';
import { knowledgeApi } from '../../lib/knowledge-api';
import { formatFileSize } from '../../lib/format-utils';

interface KnowledgeListProps {
  projectId: string;
  entries: KnowledgeEntryResponse[];
  isAdmin: boolean;
  onCreateClick: () => void;
  onEditClick: (entry: KnowledgeEntryResponse) => void;
  onDeleteClick: (entry: KnowledgeEntryResponse) => void;
  onToggleEnabled: (entry: KnowledgeEntryResponse) => void;
}

interface EntryBadgesProps {
  entry: KnowledgeEntryResponse;
}

function EntryBadges({ entry }: EntryBadgesProps) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {entry.pinned && (
        <span
          title="Pinned: always included in the agent's prompt"
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-md"
        >
          <Pin className="w-3 h-3" />
          Pinned
        </span>
      )}
      <h4 className="font-medium text-gray-900 dark:text-white truncate">{entry.title}</h4>
      <span className="px-2 py-0.5 text-xs font-medium bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-md">
        {KnowledgeCategoryLabels[entry.category]}
      </span>
      {entry.source === 'repo_import' && (
        <span
          title={entry.sourcePath ? `Imported from ${entry.sourcePath}` : 'Imported from a connected repo'}
          className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md"
        >
          Imported
        </span>
      )}
      {entry.attachment && (
        <span
          title={`${entry.attachment.filename} (${formatFileSize(entry.attachment.sizeBytes)})`}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md"
        >
          <Paperclip className="w-3 h-3" />
          {entry.attachment.filename}
        </span>
      )}
      {!entry.enabled && (
        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md">
          Disabled
        </span>
      )}
    </div>
  );
}

interface EntryActionsProps {
  entry: KnowledgeEntryResponse;
  isAdmin: boolean;
  isDownloading: boolean;
  onDownload: (entryId: string) => void;
  onEditClick: (entry: KnowledgeEntryResponse) => void;
  onDeleteClick: (entry: KnowledgeEntryResponse) => void;
  onToggleEnabled: (entry: KnowledgeEntryResponse) => void;
}

function EntryActions({
  entry,
  isAdmin,
  isDownloading,
  onDownload,
  onEditClick,
  onDeleteClick,
  onToggleEnabled,
}: EntryActionsProps) {
  return (
    <div className="flex items-center gap-3 shrink-0 text-gray-600 dark:text-gray-400">
      {entry.attachment && (
        <button
          onClick={() => onDownload(entry.id)}
          disabled={isDownloading}
          className="hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          title="Download attached file"
        >
          <Download className="w-5 h-5" />
        </button>
      )}
      {isAdmin && (
        <>
          <label className="inline-flex items-center cursor-pointer" title={entry.enabled ? 'Disable' : 'Enable'}>
            <input
              type="checkbox"
              checked={entry.enabled}
              onChange={() => onToggleEnabled(entry)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 dark:bg-gray-600 rounded-full peer-checked:bg-indigo-600 transition-colors relative">
              <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
            </div>
          </label>
          <button
            onClick={() => onEditClick(entry)}
            className="hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Edit entry"
          >
            <EditIcon />
          </button>
          <button
            onClick={() => onDeleteClick(entry)}
            className="hover:text-red-500 rounded-lg transition-colors"
            title="Delete entry"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
}

interface KnowledgeEntryCardProps {
  entry: KnowledgeEntryResponse;
  isAdmin: boolean;
  isDownloading: boolean;
  onDownload: (entryId: string) => void;
  onEditClick: (entry: KnowledgeEntryResponse) => void;
  onDeleteClick: (entry: KnowledgeEntryResponse) => void;
  onToggleEnabled: (entry: KnowledgeEntryResponse) => void;
}

function KnowledgeEntryCard({ entry, ...actionProps }: KnowledgeEntryCardProps) {
  return (
    <Card padding="sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <EntryBadges entry={entry} />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
            {entry.description}
          </p>
          {entry.phases.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {entry.phases.map((phase) => (
                <span
                  key={phase}
                  className="px-1.5 py-0.5 text-[11px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded"
                >
                  {AgentPhaseLabels[phase]}
                </span>
              ))}
            </div>
          )}
        </div>

        <EntryActions entry={entry} {...actionProps} />
      </div>
    </Card>
  );
}

export function KnowledgeList({
  projectId,
  entries,
  isAdmin,
  onCreateClick,
  onEditClick,
  onDeleteClick,
  onToggleEnabled,
}: KnowledgeListProps) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (entryId: string) => {
    setDownloadError(null);
    setDownloadingId(entryId);
    try {
      const url = await knowledgeApi.getDownloadUrl(projectId, entryId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to download knowledge attachment:', err);
      setDownloadError('Failed to download the attached file');
    } finally {
      setDownloadingId(null);
    }
  };

  if (entries.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <BookOpen className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            No knowledge base entries yet
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
            Add context about your architecture, conventions, and gotchas to help the agent work
            more accurately.
          </p>
          {isAdmin && (
            <button
              onClick={onCreateClick}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Entry
            </button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {downloadError && <p className="text-sm text-red-600 dark:text-red-400">{downloadError}</p>}
      {entries.map((entry) => (
        <KnowledgeEntryCard
          key={entry.id}
          entry={entry}
          isAdmin={isAdmin}
          isDownloading={downloadingId === entry.id}
          onDownload={handleDownload}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
          onToggleEnabled={onToggleEnabled}
        />
      ))}
    </div>
  );
}
